import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ConfirmDialog } from "@/components/common";
import { useAuth } from "@/contexts/auth-context";
import icCheck from "@/assets/icons/ic-check.svg";
import { ssoService } from "@/services/sso-service";
import { SpinnerOverlay } from "@/components/ui/spinner-overlay";

const SSO_REDIRECT_FLAG = "sso_redirect_flow";

// Popups are unreliable/blocked on mobile browsers (especially iOS Safari),
// so on mobile devices we fall back to a full-page redirect login.
const isMobileDevice = () =>
  typeof navigator !== "undefined" && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const resolveSsoErrorMessage = (error: string | null, errorDescription: string | null) =>
  error === "access_denied"
    ? "Your account has not been granted access yet. Please contact the administrator."
    : typeof errorDescription === "string"
      ? errorDescription.replace(/[\r\n]/g, "")
      : "SSO login failed. Please try again.";

export const LoginPage = () => {
  const isSsoCallback = !!(
    new URLSearchParams(window.location.search).get("code") ||
    new URLSearchParams(window.location.search).get("error")
  );

  // Detected once on mount: this tab returned from a mobile full-page redirect
  // login, so token exchange must be handled inline (no opener / popup to close).
  const [isRedirectFlow] = useState(
    () => isSsoCallback && !window.opener && sessionStorage.getItem(SSO_REDIRECT_FLAG) === "true",
  );

  const [ssoError, setSsoError] = useState<string | null>(null);
  const [ssoProcessing, setSsoProcessing] = useState(isRedirectFlow);
  const ssoHandledRef = useRef(false);
  const { handleSsoCallback } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingApproval = searchParams.get("pending") === "true";
  const [showSuccessDialog, setShowSuccessDialog] = useState(
    searchParams.get("verified") === "true",
  );

  useEffect(() => {
    if (!isSsoCallback) return;
    if (ssoHandledRef.current) return;
    ssoHandledRef.current = true;

    const code = searchParams.get("code");
    const error = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");

    // Mobile full-page redirect flow: same tab, no opener — exchange inline.
    if (isRedirectFlow) {
      sessionStorage.removeItem(SSO_REDIRECT_FLAG);

      if (code) {
        ssoService
          .exchangeToken(code)
          .then((tokens) => {
            if (!tokens.access_token) return navigate("/", { replace: true });
            return handleSsoCallback(tokens.access_token, tokens.refresh_token);
          })
          .then(() => navigate("/", { replace: true }))
          .catch((err: any) => {
            const msg =
              err.response?.data?.detail ||
              err.response?.data?.error?.message ||
              err.message ||
              "SSO 로그인 처리 실패";
            setSsoError(msg);
            setSsoProcessing(false);
            navigate("/login", { replace: true });
          });
      } else if (error) {
        setSsoError(resolveSsoErrorMessage(error, errorDescription));
        setSsoProcessing(false);
        navigate("/login", { replace: true });
      }
      return;
    }

    // Popup flow: relay the result to the parent window, then close.
    const payload = {
      type: "sso-callback",
      code,
      error,
      error_description: errorDescription,
    };

    if (window.opener) {
      window.opener.postMessage(payload, window.location.origin);
      window.close();
    } else {
      const channel = new BroadcastChannel("sso-login");
      channel.postMessage(payload);
      channel.close();
      window.close();
    }
  }, [isSsoCallback, isRedirectFlow, searchParams, handleSsoCallback, navigate]);

  const handleSsoLogin = async () => {
    setSsoError(null);
    try {
      const url = await ssoService.getLoginUrl();

      // Mobile: popups are blocked/unreliable, so navigate the whole tab.
      // We flag the flow so the /login callback knows to exchange inline.
      if (isMobileDevice()) {
        sessionStorage.setItem(SSO_REDIRECT_FLAG, "true");
        window.location.assign(url);
        return;
      }

      const popup = window.open(url, "sso-login", "width=500,height=700,left=200,top=100");
      if (!popup) {
        setSsoError("팝업이 차단되었습니다. 팝업 차단을 해제해 주세요.");
        return;
      }

      let handled = false;
      const handleSsoMessage = async (data: any) => {
        if (data?.type !== "sso-callback") return;
        if (handled) return;
        handled = true;
        cleanup();
        try {
          popup.close();
        } catch {
          /* popup may already be closed */
        }
        const { code, error, error_description } = data;

        if (code) {
          setSsoProcessing(true);
          try {
            const tokens = await ssoService.exchangeToken(code);
            if (!tokens.access_token) {
              navigate("/", { replace: true });
              return;
            }
            await handleSsoCallback(tokens.access_token, tokens.refresh_token);
            navigate("/", { replace: true });
          } catch (err: any) {
            const msg =
              err.response?.data?.detail ||
              err.response?.data?.error?.message ||
              err.message ||
              "SSO 로그인 처리 실패";
            setSsoError(msg);
            setSsoProcessing(false);
          }
        } else if (error) {
          // AADSTS50105: user not in assigned Azure AD group
          const msg =
            error === "access_denied"
              ? "Your account has not been granted access yet. Please contact the administrator."
              : typeof error_description === "string"
                ? error_description.replace(/[\r\n]/g, "")
                : "SSO login failed. Please try again.";
          setSsoError(msg);
        }
      };

      const messageListener = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        handleSsoMessage(event.data);
      };

      const channel = new BroadcastChannel("sso-login");
      channel.onmessage = (event) => handleSsoMessage(event.data);

      window.addEventListener("message", messageListener);

      const cleanup = () => {
        window.removeEventListener("message", messageListener);
        channel.close();
      };

      // Fallback: clean up after 5 minutes if no response.
      // Don't use popup.closed — Microsoft's COOP headers sever the opener
      // relationship, causing popup.closed to return true prematurely.
      setTimeout(
        () => {
          if (!handled) {
            cleanup();
          }
        },
        5 * 60 * 1000,
      );
    } catch (err: any) {
      setSsoError(err.message || "SSO 로그인을 사용할 수 없습니다.");
    }
  };

  if (isSsoCallback) {
    // Redirect flow stays in the same tab — show progress while we exchange
    // the code. The popup flow closes itself, so render nothing there.
    return isRedirectFlow ? <SpinnerOverlay /> : null;
  }

  return (
    <div className="flex flex-col items-center justify-center px-5 py-10 md:px-8 md:py-[120px]">
      <div className="w-full md:max-w-[608px] flex flex-col gap-6">
        {/* Title + Sign up link */}
        <div className="flex flex-col items-center gap-3">
          <h1 className="w-full text-center text-[44px] leading-[48px] tracking-[-0.0318em] md:text-[56px] md:leading-[64px] md:tracking-[-0.03em] font-normal text-black">
            Login
          </h1>
          <div className="flex items-center gap-2">
            <span className="text-base leading-6 tracking-[-0.01em] text-black">
              아직 계정이 없으신가요?
            </span>
            <Link
              to="/signup"
              className="text-base leading-6 tracking-[-0.01em] font-bold text-black underline"
            >
              회원가입
            </Link>
          </div>
        </div>

        {/* Pending approval banner */}
        {pendingApproval && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 text-center">
            계정 신청이 완료되었어요. 승인이 끝나면 바로 로그인할 수 있어요!
          </div>
        )}

        {/* SSO-only login */}
        <div className="flex flex-col items-center gap-6">
          <button
            type="button"
            onClick={handleSsoLogin}
            disabled={ssoProcessing}
            className="w-full md:w-[409px] h-12 flex items-center justify-center bg-[#FFAA72] rounded-[4px] text-base leading-6 tracking-[-0.01em] font-bold text-black hover:bg-[#FF9955] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <span className="md:hidden">로그인</span>
            <span className="hidden md:inline">아웃룩으로 로그인</span>
          </button>

          {/* SSO error message */}
          {ssoError && (
            <p className="mt-2 text-sm leading-5 text-[#EB2F2F] text-center">{ssoError}</p>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={showSuccessDialog}
        onOpenChange={setShowSuccessDialog}
        iconVariant="success"
        icon={<img src={icCheck} alt="" />}
        title="회원가입을 축하합니다!"
        description="이메일 인증이 완료되었습니다."
        primaryAction={{
          label: "OK",
          onClick: () => {
            setShowSuccessDialog(false);
            navigate("/login", { replace: true });
          },
        }}
      />

      {ssoProcessing && <SpinnerOverlay />}
    </div>
  );
};
